import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { ScanLine, X, Sparkles, FileText, ShieldCheck } from "lucide-react";
import { useI18n } from "@/i18n";

export default function FloatingScanCTA() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t, localePath } = useI18n();
  const [showBubble, setShowBubble] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowBubble(true), 4200); // appears after the Text CTA bubble
    return () => clearTimeout(timer);
  }, [pathname]);

  if (pathname.startsWith("/admin") || pathname === "/savings-calculator" || pathname === "/es/savings-calculator") return null;

  return (
    <div className="fixed bottom-5 left-5 sm:bottom-7 sm:left-7 z-[60] hidden sm:flex flex-col items-start gap-3">
      <AnimatePresence>
        {showBubble && !dismissed && (
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.9 }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
            className="relative hidden sm:block max-w-[260px] bg-white border border-[#E7E5DF] rounded-2xl rounded-bl-md shadow-2xl p-4 pr-8"
            data-testid="scan-cta-bubble"
          >
            <button onClick={() => setDismissed(true)} aria-label="Dismiss" className="absolute top-2.5 right-2.5 text-slate-400 hover:text-slate-700 transition-colors">
              <X size={15} />
            </button>
            <p className="text-sm text-[#0B0F14] leading-snug">
              <span className="font-bold text-emerald-700">{t("scan.bubbleBold")}</span>{t("scan.bubble")}
            </p>
            <span className="absolute -bottom-[7px] left-5 w-3.5 h-3.5 bg-white border-b border-l border-[#E7E5DF] rotate-45" />
          </motion.div>
        )}
      </AnimatePresence>

      <button
        onClick={() => navigate(localePath("/savings-calculator") + "#how-it-works")}
        data-testid="scan-cta-patent"
        className="group hidden sm:inline-flex items-center gap-1.5 pl-2.5 pr-3 py-1 rounded-full bg-white border border-[#E7E5DF] shadow-sm text-[10px] font-mono-tech uppercase tracking-wider text-slate-500 hover:text-emerald-700 hover:border-emerald-500/50 transition-colors"
      >
        <ShieldCheck size={11} className="text-emerald-600" /> {t("scan.patent")}
        <span className="text-emerald-700 font-bold group-hover:underline">{t("scan.learn")}</span>
      </button>

      <motion.div
        animate={{ y: [0, -6, 0] }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
      >
        <motion.button
          onClick={() => navigate(localePath("/savings-calculator"))}
          data-testid="floating-scan-cta"
          initial={{ opacity: 0, scale: 0, rotate: 25 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ delay: 1.1, type: "spring", stiffness: 200, damping: 14 }}
          whileHover={{ scale: 1.06 }}
          whileTap={{ scale: 0.95 }}
          className="group relative inline-flex items-center gap-2 sm:gap-2.5 pl-2.5 pr-3.5 py-2.5 sm:pl-3 sm:pr-4 sm:py-3 rounded-full bg-white text-[#0E3B3B] font-bold border border-emerald-600/25 shadow-[0_16px_40px_-8px_rgba(6,78,59,0.5)]"
        >
          <span className="absolute inset-0 rounded-full bg-emerald-400/40 animate-ping opacity-20" />
          {/* Scanning document icon */}
          <span className="relative grid place-items-center w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-[#0E3B3B] text-emerald-300 overflow-hidden">
            <FileText size={15} />
            <motion.span
              className="absolute left-1 right-1 h-[2px] bg-emerald-400 rounded-full shadow-[0_0_8px_1px_rgba(52,211,153,0.9)]"
              initial={{ top: "18%" }}
              animate={{ top: ["18%", "78%", "18%"] }}
              transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
            />
          </span>
          <span className="relative flex flex-col items-start leading-none">
            <span className="text-[12px] sm:text-[13px]">{t("scan.title")}</span>
            <span className="hidden sm:flex items-center gap-1 text-[10px] font-semibold text-emerald-600 mt-0.5">
              <Sparkles size={10} /> {t("scan.sub")}
            </span>
          </span>
          <ScanLine size={14} className="relative hidden sm:block text-emerald-600 group-hover:translate-x-0.5 transition-transform" />
        </motion.button>
      </motion.div>
    </div>
  );
}
